import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildMonthGrid, combineDateTime, dayLabel, formatDatePart, formatWib,
  monthLabel, nextRange, parseDatePart, shiftMonth, shortDay,
  todayStartWib, validateSchedule, weekdayHeaders,
} from "@/lib/wib";
import { cn } from "@/lib/utils";

const DAY_MS = 86400000;

/**
 * Kalender rentang WIB bersama (port dari prototypes/js/schedule-dialog.js).
 * Draft 100% lokal: hanya Apply yang memanggil onApply; Batal/tutup dialog
 * tidak menyentuh jadwal aktif. Bisa dipakai inline (gate katalog) maupun
 * di dalam dialog (detail/checkout/ubah jadwal).
 */
export default function RangeCalendar({
  initial, submitLabel = "Terapkan rentang", showCommitted = true,
  committedSummary = "", months = null, compact = false, onApply, onCancel,
}) {
  const today = useMemo(todayStartWib, []);
  const [view, setView] = useState(() => {
    const anchor = initial?.pickup_date && !Number.isNaN(parseDatePart(initial.pickup_date))
      ? parseDatePart(initial.pickup_date) : today;
    const d = new Date(anchor + 7 * 3600000);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  });
  const [sel, setSel] = useState(() => {
    const a = parseDatePart(initial?.pickup_date || "");
    const b = parseDatePart(initial?.return_date || "");
    return !Number.isNaN(a) && !Number.isNaN(b) && a <= b ? { start: a, end: b } : { start: null, end: null };
  });
  const [tAmbil, setTAmbil] = useState(initial?.pickup_time || "09:00");
  const [tKembali, setTKembali] = useState(initial?.return_time || "17:00");
  const [errors, setErrors] = useState([]);
  const [isPhone, setIsPhone] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  const focusDayRef = useRef(null);
  const errorBoxRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Kembalikan fokus setelah render ulang (navigasi bulan/resize).
  useEffect(() => {
    if (focusDayRef.current == null) return;
    const el = gridRef.current?.querySelector(`[data-day="${focusDayRef.current}"]`);
    focusDayRef.current = null;
    if (el && !el.disabled && el.offsetParent !== null) el.focus({ preventScroll: true });
  });

  const shown = months ?? (isPhone ? 1 : 2);
  const headers = useMemo(weekdayHeaders, []);

  const cells = useCallback((k) => {
    const { year, month } = shiftMonth(view.year, view.month, k);
    return { year, month, list: buildMonthGrid(year, month, today) };
  }, [view, today]);

  const visibleEnabled = useMemo(() => {
    const out = [];
    for (let k = 0; k < shown; k++)
      out.push(...cells(k).list.filter((c) => c.inMonth && !c.disabled));
    return out;
  }, [cells, shown]);

  const activeDay = useMemo(() => {
    if (sel.start != null && visibleEnabled.some((c) => c.dayMs === sel.start)) return sel.start;
    if (visibleEnabled.some((c) => c.dayMs === today)) return today;
    return visibleEnabled.length ? visibleEnabled[0].dayMs : null;
  }, [sel, visibleEnabled, today]);

  const dayAria = (cell) => {
    const bits = [dayLabel(cell.dayMs)];
    if (cell.dayMs === today) bits.push("hari ini");
    if (cell.disabled) bits.push("tanggal lampau, tidak dapat dipilih");
    else {
      if (sel.start === cell.dayMs && sel.end === cell.dayMs) bits.push("tanggal ambil sekaligus tanggal kembali");
      else if (sel.start === cell.dayMs) bits.push("tanggal ambil yang dipilih");
      else if (sel.end === cell.dayMs) bits.push("tanggal kembali yang dipilih");
      else if (sel.start != null && sel.end != null && cell.dayMs > sel.start && cell.dayMs < sel.end)
        bits.push("dalam rentang yang dipilih");
    }
    return bits.join(", ");
  };

  const selectionText = () => {
    if (sel.start == null) return "Pilih tanggal ambil pada kalender.";
    if (sel.end == null) return `${dayLabel(sel.start)} dipilih sebagai tanggal ambil. Pilih tanggal kembali.`;
    const a = combineDateTime(formatDatePart(sel.start), tAmbil);
    const b = combineDateTime(formatDatePart(sel.end), tKembali);
    return a && b ? `${formatWib(parseWib(a))} → ${formatWib(parseWib(b))}.` : `${dayLabel(sel.start)} → ${dayLabel(sel.end)}.`;
  };
  const parseWib = (v) => {
    const [d, t] = v.split("T");
    const day = parseDatePart(d);
    const [h, mi] = t.split(":").map(Number);
    return day + h * 3600000 + mi * 60000;
  };

  const showErrors = (list) => {
    setErrors(list);
    setTimeout(() => errorBoxRef.current?.focus(), 0);
  };

  const doApply = () => {
    if (sel.start == null || sel.end == null) {
      showErrors(["Pilih tanggal ambil dan tanggal kembali pada kalender."]);
      return;
    }
    const pickup = combineDateTime(formatDatePart(sel.start), tAmbil);
    const ret = combineDateTime(formatDatePart(sel.end), tKembali);
    const problems = [];
    if (!pickup) problems.push("Jam mulai tidak valid (format jam:menit). Nilai tidak diubah.");
    if (!ret) problems.push("Jam selesai tidak valid (format jam:menit). Nilai tidak diubah.");
    if (problems.length) { showErrors(problems); return; }
    const v = validateSchedule(pickup, ret);
    if (!v.ok) {
      showErrors([v.errors.pickup, v.errors.return].filter(Boolean));
      return;
    }
    onApply({
      pickup_date: formatDatePart(sel.start), return_date: formatDatePart(sel.end),
      pickup_time: tAmbil, return_time: tKembali,
    });
  };

  const doCancel = () => {
    setSel({ start: null, end: null });
    setTAmbil("09:00");
    setTKembali("17:00");
    setErrors([]);
    onCancel?.();
  };

  const pick = (dayMs) => { setSel((s) => nextRange(s, dayMs)); };

  // Satu rantai if/else yang saling lepas: ujung tunggal atau hari-sama
  // selalu simetris penuh; rounding searah hanya untuk rentang lengkap
  // dengan dua tanggal berbeda.
  const dayCellClass = (dayMs, cell) => {
    const base = "min-h-[44px] rounded-md border border-transparent p-0 text-sm text-slate-900 transition-colors hover:bg-slate-100 hover:border-slate-300";
    const endpoint = "bg-[#0067c8] font-bold text-white hover:bg-[#0067c8] hover:text-white";
    const isStart = sel.start === dayMs;
    const isEnd = sel.end === dayMs;
    const complete = sel.start != null && sel.end != null;
    const sameDay = complete && sel.start === sel.end;
    const inBand = complete && !sameDay && dayMs > sel.start && dayMs < sel.end;
    let shape = "";
    if (inBand) return cn(base, "rounded-none bg-[#e7f1fc] hover:bg-[#e7f1fc]");
    if (sameDay && isStart) shape = "rounded-md";
    else if (complete && !sameDay && isStart) shape = "rounded-r-none";
    else if (complete && !sameDay && isEnd) shape = "rounded-l-none";
    else if (!complete && (isStart || isEnd)) shape = "rounded-md";
    return cn(
      base,
      (isStart || isEnd) && endpoint,
      shape,
      dayMs === today && "font-bold underline underline-offset-[3px]",
      (isStart || isEnd) && dayMs === today && "decoration-white",
      cell.disabled && "cursor-not-allowed text-slate-300 hover:bg-transparent hover:border-transparent",
    );
  };

  const ensureVisible = (dayMs) => {
    const months = [];
    for (let k = 0; k < shown; k++) {
      const { year, month } = shiftMonth(view.year, view.month, k);
      months.push(...buildMonthGrid(year, month, today).filter((c) => c.inMonth).map((c) => c.dayMs));
    }
    if (dayMs < months[0]) {
      const d = new Date(dayMs + 7 * 3600000);
      setView({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    } else if (dayMs > months[months.length - 1]) {
      const d = new Date(dayMs + 7 * 3600000);
      const cur = shiftMonth(d.getUTCFullYear(), d.getUTCMonth(), -(shown - 1));
      setView({ year: cur.year, month: cur.month });
    }
  };

  const stepDay = (fromMs, delta) => {
    let day = fromMs + delta * DAY_MS;
    for (let i = 0; i < 45 && day < today; i++) day += (delta > 0 ? 1 : -1) * DAY_MS;
    return day < today ? fromMs : day;
  };

  const focusDay = (dayMs) => { ensureVisible(dayMs); focusDayRef.current = dayMs; };

  const onGridKeyDown = (e) => {
    const btn = e.target.closest("[data-day]");
    if (!btn) return;
    const day = Number(btn.dataset.day);
    const d = new Date(day + 7 * 3600000);
    let handled = true;
    if (e.key === "ArrowLeft") focusDay(stepDay(day, -1));
    else if (e.key === "ArrowRight") focusDay(stepDay(day, 1));
    else if (e.key === "ArrowUp") focusDay(stepDay(day, -7));
    else if (e.key === "ArrowDown") focusDay(stepDay(day, 7));
    else if (e.key === "Home") focusDay(Math.max(day - ((d.getUTCDay() + 6) % 7) * DAY_MS, today));
    else if (e.key === "End") focusDay(day + (6 - ((d.getUTCDay() + 6) % 7)) * DAY_MS);
    else if (e.key === "PageUp" || e.key === "PageDown") {
      const delta = e.key === "PageUp" ? -1 : 1;
      const cur = shiftMonth(view.year, view.month, delta);
      setView({ year: cur.year, month: cur.month });
      const nd = new Date(day + 7 * 3600000);
      const dim = new Date(Date.UTC(nd.getUTCFullYear(), nd.getUTCMonth() + delta + 1, 0)).getUTCDate();
      focusDay(Math.max(Date.UTC(nd.getUTCFullYear(), nd.getUTCMonth() + delta, Math.min(nd.getUTCDate(), dim)) - 7 * 3600000, today));
    } else handled = false;
    if (handled) e.preventDefault();
  };

  const nav = (delta) => {
    const cur = shiftMonth(view.year, view.month, delta);
    setView({ year: cur.year, month: cur.month });
  };

  const quickDay = (offset) => {
    const day = today + offset * DAY_MS;
    setSel({ start: day, end: day });
    ensureVisible(day);
    focusDayRef.current = day;
  };
  const wibTodayStr = formatDatePart(today);
  const addDaysStr = (dateStr, n) => {
    const base = parseDatePart(dateStr);
    return formatDatePart(base + n * DAY_MS);
  };

  return (
    <div className="schedule-picker">
      {showCommitted && (
        <p className="mb-3 text-[13px] text-slate-500">
          {committedSummary || "Belum ada jadwal aktif."}
        </p>
      )}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-medium text-slate-500">Cepat:</span>
        {["Hari ini", "Besok", "Lusa"].map((label, i) => {
          const qd = today + i * DAY_MS;
          const active = sel.start === qd && sel.end === qd;
          return (
            <button key={label} type="button" onClick={() => quickDay(i)} aria-pressed={active}
              className={active
                ? "min-h-[44px] rounded-full border border-[#0067c8] bg-[#e7f1fc] px-3.5 py-1.5 text-xs font-bold text-[#004f9e] transition-colors"
                : "min-h-[44px] rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-900"}>
              {label}
            </button>
          );
        })}
        <span className="sr-only">Tanggal acuan WIB hari ini {wibTodayStr}; lusa {addDaysStr(wibTodayStr, 2)}</span>
      </div>
      <div className="mb-2 flex justify-between gap-2">
        <button type="button" onClick={() => nav(-1)}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] font-semibold text-slate-800 transition-colors hover:border-slate-900">
          ‹ Bulan sebelumnya
        </button>
        <button type="button" onClick={() => nav(1)}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] font-semibold text-slate-800 transition-colors hover:border-slate-900">
          Bulan berikutnya ›
        </button>
      </div>
      <div ref={gridRef} onKeyDown={onGridKeyDown} className={cn("grid grid-cols-1", shown > 1 && "gap-4 sm:grid-cols-2", compact && "gap-3")}>
        {Array.from({ length: shown }).map((_, k) => {
          const { year, month, list } = cells(k);
          return (
            <section key={`${year}-${month}`} aria-label={monthLabel(year, month)}>
              <h3 className={cn("font-display text-[15px] font-semibold text-slate-900", compact ? "mb-1" : "mb-2")}>{monthLabel(year, month)}</h3>
              <div className="mb-1 grid grid-cols-7 text-center text-xs text-slate-500" aria-hidden="true">
                {headers.map((w) => <span key={w}>{w}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-[2px] tabular-nums" role="group" aria-label={monthLabel(year, month)}>
                {list.map((cell) => cell.inMonth ? (
                  <button key={cell.dayMs} type="button" data-day={cell.dayMs}
                    tabIndex={cell.dayMs === activeDay ? 0 : -1}
                    disabled={cell.disabled} onClick={() => pick(cell.dayMs)}
                    aria-label={dayAria(cell)}
                    aria-pressed={cell.dayMs === sel.start || cell.dayMs === sel.end}
                    className={dayCellClass(cell.dayMs, cell)}>
                    {new Date(cell.dayMs + 7 * 3600000).getUTCDate()}
                  </button>
                ) : (
                  <span key={cell.dayMs} aria-hidden="true" />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <div className={cn("grid grid-cols-1 gap-3 min-[380px]:grid-cols-2", compact ? "mt-2" : "mt-3")}>
        <div>
          <span className="block text-[13px] font-semibold text-[#004f9e]" aria-hidden="true">
            {sel.start != null ? shortDay(sel.start) : "Pilih tanggal dulu"}
          </span>
          <label htmlFor="cal-ambil" className="mb-1.5 mt-0.5 block text-sm font-semibold text-slate-800">Jam mulai (WIB)</label>
          <input id="cal-ambil" data-testid="schedule-time-pickup" type="time" value={tAmbil} required
            aria-describedby="cal-ambil-tgl"
            onChange={(e) => setTAmbil(e.target.value)}
            className="h-[44px] w-full rounded-lg border border-slate-300 bg-white px-2.5 text-slate-900" />
          <span id="cal-ambil-tgl" className="sr-only">
            Tanggal mulai: {sel.start != null ? dayLabel(sel.start) : "belum dipilih"}
          </span>
        </div>
        <div>
          <span className="block text-[13px] font-semibold text-[#004f9e]" aria-hidden="true">
            {sel.end != null ? shortDay(sel.end) : "Pilih tanggal dulu"}
          </span>
          <label htmlFor="cal-kembali" className="mb-1.5 mt-0.5 block text-sm font-semibold text-slate-800">Jam selesai (WIB)</label>
          <input id="cal-kembali" data-testid="schedule-time-return" type="time" value={tKembali} required
            aria-describedby="cal-kembali-tgl"
            onChange={(e) => setTKembali(e.target.value)}
            className="h-[44px] w-full rounded-lg border border-slate-300 bg-white px-2.5 text-slate-900" />
          <span id="cal-kembali-tgl" className="sr-only">
            Tanggal selesai: {sel.end != null ? dayLabel(sel.end) : "belum dipilih"}
          </span>
        </div>
      </div>
      <p className={cn("text-sm text-slate-600", compact ? "mt-2" : "mt-3")} role="status">{selectionText()}</p>
      {errors.length > 0 && (
        <div ref={errorBoxRef} tabIndex={-1} role="alert"
          className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <p><strong>Rentang belum bisa diterapkan.</strong> Draf Anda tidak berubah.</p>
          <ul className="mt-2 list-disc pl-5">{errors.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      <div className={cn("flex flex-wrap justify-end gap-2.5", compact ? "mt-4" : "mt-5")}>
        <button type="button" onClick={doCancel}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-[18px] py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-900">
          Batal
        </button>
        <button type="button" onClick={doApply}
          className="min-h-[44px] rounded-lg border border-[#0067c8] bg-[#0067c8] px-[18px] py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#004f9e] hover:bg-[#004f9e] active:border-[#00417e] active:bg-[#00417e]">
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
